import React, { useState, useCallback, useRef, useEffect } from 'react';
import Papa from 'papaparse';
import { User, UserRole, UserExtraRole } from '../types';
import Button from './ui/Button';
import Modal from './ui/Modal';
import Badge from './ui/Badge';
import Alert from './ui/Alert';
import ProgressBar from './ui/ProgressBar';
import CopyButton from './ui/CopyButton';
import { CloudArrowUpIcon } from './icons/CloudArrowUpIcon';
import { CheckIcon } from './icons/CheckIcon';
import { ExclamationTriangleIcon } from './icons/ExclamationTriangleIcon';
import { XMarkIcon } from './icons/MaterialIcons';
import { api } from '../services/apiService';
import { logger } from '../utils/logger';
import { classifyError, logError } from '../utils/errorHandler';
import { unifiedNotificationManager } from '../services/notifications/unifiedNotificationManager';
import { BYTES_PER_KB, UI_DELAYS } from '../constants';

export interface CSVRow {
  [key: string]: string;
}

export interface ParsedUser extends Partial<User> {
  isValid: boolean;
  errors: string[];
  rowIndex: number;
}

export interface ImportResult {
  total: number;
  success: number;
  failed: number;
  errors: Array<{ row: number; email: string; error: string }>;
}

interface UserImportProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete?: () => void;
}

const UserImport: React.FC<UserImportProps> = ({ isOpen, onClose, onImportComplete }) => {
  const [step, setStep] = useState<'upload' | 'preview' | 'importing' | 'complete'>('upload');
  const [parsedUsers, setParsedUsers] = useState<ParsedUser[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [importProgress, setImportProgress] = useState(0);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setStep('upload');
      setParsedUsers([]);
      setFile(null);
      setImportProgress(0);
      setImportResult(null);
      setIsClosing(false);
      setUploadError(null);
    }
  }, [isOpen]);

  const validateUser = useCallback((row: CSVRow, index: number): ParsedUser => {
    const validateEmail = (email: string): boolean => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    };

    const errors: string[] = [];
    const name = row['name'] || row['Nama'] || row['NAMA'] || '';
    const email = row['email'] || row['Email'] || row['EMAIL'] || '';
    const roleStr = row['role'] || row['Role'] || row['ROLE'] || 'student';
    const status = (row['status'] || row['Status'] || row['STATUS'] || 'active') as 'active' | 'inactive';
    const extraRoleStr = row['extraRole'] || row['extra_role'] || row['Extra Role'] || '';

    if (!name || name.trim().length === 0) {
      errors.push('Name is required');
    }

    if (!email || !validateEmail(email)) {
      errors.push('Valid email is required');
    }

    const validRoles: UserRole[] = ['admin', 'teacher', 'student', 'parent'];
    const role: UserRole = validRoles.includes(roleStr.toLowerCase() as UserRole)
      ? (roleStr.toLowerCase() as UserRole)
      : 'student';

    let extraRole: UserExtraRole | null = null;
    if (extraRoleStr) {
      const validExtraRoles: UserExtraRole[] = ['staff', 'osis', 'wakasek', 'kepsek'];
      if (validExtraRoles.includes(extraRoleStr.toLowerCase() as UserExtraRole)) {
        extraRole = extraRoleStr.toLowerCase() as UserExtraRole;
      }
    }

    const validStatuses: ('active' | 'inactive')[] = ['active', 'inactive'];
    const userStatus = validStatuses.includes(status) ? status : 'active';

    return {
      name: name.trim(),
      email: email.trim().toLowerCase(),
      role,
      extraRole,
      status: userStatus,
      isValid: errors.length === 0,
      errors,
      rowIndex: index + 2,
    };
  }, []);

  const parseCSV = useCallback((file: File): Promise<CSVRow[]> => {
    return new Promise((resolve, reject) => {
      Papa.parse<CSVRow>(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          if (results.errors.length > 0) {
            logger.error('CSV parsing errors:', results.errors);
            reject(new Error(`CSV parsing failed: ${results.errors[0].message}`));
            return;
          }
          resolve(results.data || []);
        },
        error: (error) => {
          logger.error('CSV parse error:', error);
          reject(error);
        },
      });
    });
  }, []);

  const handleFileSelect = useCallback(async (selectedFile: File | null) => {
    if (!selectedFile) return;

    setUploadError(null);

    if (!selectedFile.name.endsWith('.csv')) {
      setUploadError('Format file tidak valid. Harap pilih file CSV (.csv)');
      logger.error('Invalid file type: Please select a CSV file');
      return;
    }

    try {
      const data = await parseCSV(selectedFile);
      if (data.length === 0) {
        setUploadError('File CSV kosong. Harap pastikan file memiliki data yang valid.');
        logger.error('CSV file is empty');
        return;
      }

      const validatedUsers = data.map((row, index) => validateUser(row, index));
      setParsedUsers(validatedUsers);
      setFile(selectedFile);
      setUploadError(null);
      setStep('preview');
    } catch (error) {
      const classifiedError = classifyError(error, {
        operation: 'parseCSV',
        timestamp: Date.now(),
      });
      logError(classifiedError);
      setUploadError('Gagal memproses file CSV. Harap periksa format file dan coba lagi.');
      logger.error('Failed to parse CSV file. Please check format and try again.');
    }
  }, [parseCSV, validateUser]);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, [handleFileSelect]);

  const handleImport = async () => {
    const validUsers = parsedUsers.filter((user) => user.isValid);

    if (validUsers.length === 0) {
      logger.error('No valid users to import');
      return;
    }

    setStep('importing');
    setImportProgress(0);

    const result: ImportResult = {
      total: validUsers.length,
      success: 0,
      failed: 0,
      errors: [],
    };

    for (let i = 0; i < validUsers.length; i++) {
      const user = validUsers[i];
      setImportProgress(Math.round(((i + 1) / validUsers.length) * 100));

      try {
        const response = await api.users.create(user as User);
        if (response.success) {
          result.success++;
        } else {
          result.failed++;
          result.errors.push({
            row: user.rowIndex,
            email: user.email || 'N/A',
            error: response.message || 'Unknown error',
          });
        }
      } catch (error) {
        result.failed++;
        result.errors.push({
          row: user.rowIndex,
          email: user.email || 'N/A',
          error: error instanceof Error ? error.message : 'Unknown error',
        });
        logger.error(`Failed to import user at row ${user.rowIndex}:`, error);
      }

      await new Promise((resolve) => setTimeout(resolve, UI_DELAYS.USER_IMPORT_DELAY));
    }

    setImportResult(result);
    setStep('complete');

    if (result.success > 0) {
      unifiedNotificationManager.showNotification({
        id: `bulk-import-${Date.now()}`,
        type: 'system',
        title: 'Bulk Import Complete',
        body: `${result.success} users imported successfully${result.failed > 0 ? `, ${result.failed} failed` : ''}`,
        priority: result.failed > 0 ? 'normal' : 'high',
        timestamp: new Date().toISOString(),
        read: false
      });
      onImportComplete?.();
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    onClose();
  };

  const validUserCount = parsedUsers.filter((user) => user.isValid).length;
  const invalidUserCount = parsedUsers.filter((user) => !user.isValid).length;

  // Format import errors as CSV report for easy copying
  const formatErrorReport = useCallback((): string => {
    if (!importResult || importResult.errors.length === 0) return '';
    
    const headers = ['Row', 'Email', 'Error Message'];
    const rows = importResult.errors.map(error => 
      `${error.row},"${error.email}","${error.error.replace(/"/g, '""')}"`
    );
    
    return [headers.join(','), ...rows].join('\n');
  }, [importResult]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={step === 'complete' ? 'Import Complete' : 'Import Users from CSV'}
      size="lg"
    >
      {step === 'upload' && (
        <div className="space-y-6">
          <div className="text-neutral-600 dark:text-neutral-400">
            <p className="mb-4">Upload a CSV file with user data. The CSV should include the following columns:</p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li><code>name</code> (required) - User's full name</li>
              <li><code>email</code> (required) - User's email address</li>
              <li><code>role</code> (optional, default: student) - admin, teacher, student, or parent</li>
              <li><code>extraRole</code> (optional) - staff, osis, wakasek, or kepsek</li>
              <li><code>status</code> (optional, default: active) - active or inactive</li>
            </ul>
          </div>

          <Button
            variant="secondary"
            onClick={() => {
              const csvContent = 'name,email,role,extraRole,status\nJohn Doe,john@example.com,teacher,,active\nJane Smith,jane@example.com,student,,active\nAdmin User,admin@example.com,admin,,active';
              const blob = new Blob([csvContent], { type: 'text/csv' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'users_template.csv';
              a.click();
              URL.revokeObjectURL(url);
            }}
          >
            Download CSV Template
          </Button>

          <div
            role="button"
            aria-label="Upload CSV file"
            tabIndex={0}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
            className={`border-2 border-dashed rounded-xl p-12 flex flex-col items-center justify-center text-center cursor-pointer transition-all ${
              dragActive
                ? 'border-primary-400 bg-primary-50/50 dark:bg-primary-900/10'
                : 'border-neutral-300 dark:border-neutral-600 hover:border-primary-400 hover:bg-primary-50/50 dark:hover:bg-primary-900/10'
            }`}
          >
            <CloudArrowUpIcon className="w-12 h-12 text-neutral-400 dark:text-neutral-500 mb-4" />
            <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Click to upload or drag and drop
            </p>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              CSV file only
            </p>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={(e) => e.target.files && handleFileSelect(e.target.files[0])}
            className="hidden"
          />

          {uploadError && (
            <Alert variant="error" className="animate-in fade-in slide-in-from-top-2 duration-300">
              <p>{uploadError}</p>
            </Alert>
          )}

          {file && (
            <div className="flex items-center justify-between p-3 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg">
              <div className="flex items-center gap-3">
                <span className="text-2xl">📄</span>
                <div>
                  <p className="text-sm font-medium text-neutral-900 dark:text-white truncate">
                    {file.name}
                  </p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">
                    {(file.size / BYTES_PER_KB).toFixed(2)} KB
                  </p>
                </div>
              </div>
              <Button
                variant="danger"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setFile(null);
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }}
                iconOnly
                icon={<XMarkIcon className="w-4 h-4" />}
              />
            </div>
          )}
        </div>
      )}

      {step === 'preview' && (
        <div className="space-y-4">
          <Alert variant="info">
            <p>Found {validUserCount} valid users and {invalidUserCount} invalid rows.</p>
          </Alert>

          {invalidUserCount > 0 && (
            <Alert variant="warning">
              <p>Some rows have validation errors and will not be imported. Review the errors below.</p>
            </Alert>
          )}

          <div className="max-h-96 overflow-y-auto border border-neutral-200 dark:border-neutral-700 rounded-lg">
            <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-700">
              <thead className="bg-neutral-50 dark:bg-neutral-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Row</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Errors</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-neutral-900 divide-y divide-neutral-200 dark:divide-neutral-700">
                {parsedUsers.map((user: { isValid: boolean; name?: string; email?: string; role?: string; errors?: string[] }, index: number) => (
                  <tr key={index} className={user.isValid ? '' : 'bg-red-50 dark:bg-red-900/20'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">{index + 1}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-900 dark:text-white">{user.name || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">{user.email || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">{user.role || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <Badge variant={user.isValid ? 'default' : 'error'} size="sm">
                        {user.isValid ? 'Valid' : 'Invalid'}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs text-neutral-500">
                      {user.errors && user.errors.length > 0 ? user.errors.join(', ') : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between pt-4 border-t border-neutral-200 dark:border-neutral-700">
            <Button
              variant="secondary"
              onClick={() => {
                setStep('upload');
                setUploadError(null);
              }}
              disabled={isClosing}
            >
              Back
            </Button>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={handleClose}
                disabled={isClosing}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleImport}
                disabled={validUserCount === 0 || isClosing}
              >
                Import {validUserCount} Users
              </Button>
            </div>
          </div>
        </div>
      )}

      {step === 'importing' && (
        <div className="space-y-6 py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-4" />
            <p className="text-lg font-medium text-neutral-900 dark:text-white mb-2">
              Importing Users...
            </p>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              Please wait while we process the import
            </p>
          </div>

          <ProgressBar
            value={importProgress}
            size="lg"
            className="max-w-md mx-auto"
            aria-label={`Import progress: ${importProgress}%`}
          />

          <p className="text-center text-sm text-neutral-600 dark:text-neutral-400">
            {importProgress}% complete
          </p>
        </div>
      )}

      {step === 'complete' && importResult && (
        <div className="space-y-6">
          <div className="text-center py-4">
            {importResult.failed === 0 ? (
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckIcon className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
            ) : (
              <div className="w-16 h-16 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <ExclamationTriangleIcon className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
              </div>
            )}
            <h3 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">
              Import {importResult.failed === 0 ? 'Successful' : 'Complete'}
            </h3>
            <p className="text-neutral-600 dark:text-neutral-400">
              {importResult.success} of {importResult.total} users imported successfully
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-green-600 dark:text-green-400">{importResult.success}</p>
              <p className="text-sm text-green-700 dark:text-green-300">Successful</p>
            </div>
            {importResult.failed > 0 && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-red-600 dark:text-red-400">{importResult.failed}</p>
                <p className="text-sm text-red-700 dark:text-red-300">Failed</p>
              </div>
            )}
          </div>

          {importResult.errors.length > 0 && (
            <div className="max-h-64 overflow-y-auto border border-neutral-200 dark:border-neutral-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-neutral-900 dark:text-white">
                  Import Errors ({importResult.errors.length})
                </h4>
                <CopyButton
                  text={formatErrorReport()}
                  variant="secondary"
                  size="sm"
                  tooltipPosition="left"
                  successMessage="Error report copied!"
                  ariaLabel="Copy error report to clipboard"
                  onCopied={(success) => {
                    if (success) {
                      unifiedNotificationManager.showNotification({
                        id: `error-report-copied-${Date.now()}`,
                        type: 'system',
                        title: 'Error Report Copied',
                        body: 'Error details copied to clipboard as CSV format',
                        priority: 'low',
                        timestamp: new Date().toISOString(),
                        read: false
                      });
                    }
                  }}
                />
              </div>
              <div className="space-y-2">
                {importResult.errors.map((error, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-2 text-sm p-2 bg-red-50 dark:bg-red-900/10 rounded"
                  >
                    <ExclamationTriangleIcon className="w-4 h-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-neutral-900 dark:text-white">
                        Row {error.row}: {error.email}
                      </p>
                      <p className="text-neutral-600 dark:text-neutral-400 text-xs">
                        {error.error}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-xs text-neutral-500 dark:text-neutral-400 italic">
                Copy the error report to fix these issues in your CSV file and re-import.
              </p>
            </div>
          )}

          <div className="flex justify-end pt-4 border-t border-neutral-200 dark:border-neutral-700">
            <Button
              variant="primary"
              onClick={handleClose}
              disabled={isClosing}
            >
              Close
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default UserImport;
